// CustomerSyncService — the ONLY place allowed to mutate customer statistics.
// Every module (orders, CSV import, complaints, …) routes customer updates here
// so calculations are identical regardless of source.
//
// Data safety (Phase 10): uses field-level update() patches. It NEVER overwrites
// the whole customer object and NEVER touches nested branches it doesn't own
// (remarks, complaints, timeline, orders, addresses, tasks, manual edits).
import { db } from '../../integrations/firebase';
import { ref, get, update } from 'firebase/database';
import { customerKey } from './CustomerEventEngine';
import { applyOrderToStats, applyStatusToStats } from './CustomerAnalyticsEngine';
import { recordTimelineEvent, TIMELINE_EVENTS } from './CustomerTimelineEngine';
import { buildOrderSnapshot, sanitizeOrderId } from './orderSnapshot';

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));

// Only the analytics fields this service owns — written as a flat field-level patch.
const STAT_FIELDS = [
    'totalOrders', 'totalRevenue', 'lifetimeValue', 'averageOrderValue',
    'firstOrderDate', 'lastOrderDate', 'repeatCustomer', 'customerType',
    'paymentCounts', 'preferredPayment', 'productCounts', 'preferredProduct',
    'courierCounts', 'preferredCourier', 'deliveryStats', 'deliverySuccessPct',
    'refundStats', 'rfm', 'churnRisk', 'healthScore', 'riskScore',
    // Phase 2 flat status counters / rates / timestamps
    'deliveredOrders', 'cancelledOrders', 'rtoOrders', 'refundedOrders',
    'replacementOrders', 'reshipOrders', 'deliverySuccessRate', 'rtoRate',
    'refundRate', 'lastDeliveredAt', 'lastRtoAt', 'lastRefundAt',
];

// Idempotency key for a status event — keyed by (orderId, status) ONLY so the
// same status counts once regardless of which producer fires it (complaint /
// orderlist / tracking). Sanitized for a Firebase path.
const statusEventKey = (orderId, status /*, source */) =>
    `${orderId || 'order'}_${status}`.replace(/[.#$[\]/\s]/g, '_');
const pickStats = (s) => clean(Object.fromEntries(STAT_FIELDS.map(k => [k, s[k]])));

const readCustomer = async (key) => {
    const snap = await get(ref(db, `customerBase/${key}`));
    return snap.exists() ? snap.val() : null;
};

// Add an address only if it isn't already present (preserves manual edits).
const addressPatch = (existing, c) => {
    if (!c || !(c.address || c.pincode)) return null;
    const addresses = Array.isArray(existing?.addresses) ? existing.addresses : [];
    const dup = addresses.some(a => (a.fullAddress || a.address) === c.address && (a.pincode || '') === (c.pincode || ''));
    if (dup) return null;
    return [...addresses, clean({ fullAddress: c.address, city: c.city, state: c.state, pincode: c.pincode })];
};

export const CustomerSyncService = {
    customerKey,

    /**
     * Live customer create/update from a newly-created order (Phase 2 & 3).
     * Idempotency note: call once per order creation.
     */
    syncCustomerFromOrder: async (order, user) => {
        const c = order?.customer || {};
        const key = customerKey(c.mobile);
        if (!key) return { ok: false, reason: 'no-mobile' };

        try {
            const existing = await readCustomer(key);
            const isNew = !existing;
            const nextStats = applyOrderToStats(existing || {}, order);

            const patch = { ...pickStats(nextStats), primaryMobile: c.mobile, updatedAt: Date.now() };
            // Identity: only fill when missing — do NOT clobber manual edits.
            if (!existing?.name && c.name) patch.name = c.name;
            if (!existing?.city && c.city) patch.city = c.city;
            if (!existing?.state && c.state) patch.state = c.state;
            if (!existing?.pincode && c.pincode) patch.pincode = c.pincode;
            if (!existing?.secondaryMobile && c.alternateMobile) patch.secondaryMobile = c.alternateMobile;
            const addrs = addressPatch(existing, c);
            if (addrs) patch.addresses = addrs;

            await update(ref(db, `customerBase/${key}`), clean(patch));

            // Write the nested order snapshot so it appears in Recent Orders
            // immediately (keyed by orderId → idempotent, no duplicates).
            const snapId = sanitizeOrderId(order.id || order.firebaseKey || '');
            if (snapId) await update(ref(db, `customerBase/${key}/orders/${snapId}`), buildOrderSnapshot(order, order.firebaseKey || order.id));

            const by = user?.uid || user?.email || 'system';
            if (isNew) await recordTimelineEvent(c.mobile, TIMELINE_EVENTS.CUSTOMER_CREATED, { note: 'Customer created from order', source: order.source || 'app' }, by);
            await recordTimelineEvent(c.mobile, TIMELINE_EVENTS.ORDER_CREATED, {
                orderId: order.id, amount: order?.payment?.total, status: order.status, note: `Order ${order.id || ''}`,
            }, by);

            return { ok: true, key, isNew, repeatCustomer: nextStats.repeatCustomer };
        } catch (e) {
            console.warn('[CustomerSync] syncCustomerFromOrder failed:', e.message);
            return { ok: false, reason: e.message };
        }
    },

    /**
     * Apply an order status change to customer stats — IDEMPOTENT.
     * Statuses: delivered | cancelled | returned | refunded | replacement | reship.
     * The same (orderId,status,source) event processed twice is a no-op (guarded by
     * customerBase/{key}/processedEvents/{eventKey}).
     */
    applyOrderStatusEvent: async (mobile, status, { orderId, source, user } = {}) => {
        const key = customerKey(mobile);
        if (!key) return { ok: false, reason: 'no-mobile' };
        const evKey = statusEventKey(orderId, status, source);
        try {
            const existing = await readCustomer(key);
            if (!existing) return { ok: false, reason: 'no-customer' };
            // Idempotency guard
            if (existing.processedEvents && existing.processedEvents[evKey]) {
                return { ok: true, skipped: true, key };
            }

            const nextStats = applyStatusToStats(existing, status);
            await update(ref(db, `customerBase/${key}`), {
                ...pickStats(nextStats),
                [`processedEvents/${evKey}`]: true,
                updatedAt: Date.now(),
            });

            const TL = TIMELINE_EVENTS;
            const map = {
                delivered: 'order_delivered', cancelled: 'order_cancelled', returned: 'order_rto',
                refunded: 'refund_issued', replacement: 'replacement_created', reship: 'reship_created',
            };
            if (map[status]) await recordTimelineEvent(mobile, map[status], { orderId, status, source }, user?.uid || 'system');
            return { ok: true, key, eventKey: evKey };
        } catch (e) {
            console.warn('[CustomerSync] applyOrderStatusEvent failed:', e.message);
            return { ok: false, reason: e.message };
        }
    },

    // Courier status update (non-terminal) → timeline only, no stat change.
    recordCourierStatus: async (mobile, { orderId, courier, status, awb, user } = {}) => {
        if (!customerKey(mobile)) return { ok: false };
        await recordTimelineEvent(mobile, 'courier_status_updated', { orderId, courier, status, awb }, user?.uid || 'system');
        return { ok: true };
    },

    // Re-export so other modules write the unified timeline through one place.
    recordTimelineEvent,
    TIMELINE_EVENTS,
};

export default CustomerSyncService;
